import type { ProjectInvoice } from '@/types'

/**
 * Mirrors the BE domain rule `ProjectInvoicing.GetFinalId()` which excludes
 * superseded finals — a final superseded by a Mode A credit note no longer
 * blocks redo, so the FE must filter the same way to surface the
 * "Nouvelle facture" / "Facture finale" affordances.
 */
export function hasActiveFinalInvoice(
  invoices: ReadonlyArray<Pick<ProjectInvoice, 'type' | 'isSuperseded'>>,
): boolean {
  return invoices.some((i) => i.type === 'final' && !i.isSuperseded)
}

/**
 * SOLY-ORDER-002 fiche 16 — does the project still owe an invoice?
 *
 * Derived from the balance alone, never from a status (AC-1): a deleted sale (whose reference total
 * the BE zeroes, fiche 15), a fully credited project and a fully invoiced one all reach the same
 * balance, so they all read "Rien à facturer" for the same reason. Coupling this to "sale deleted"
 * would recreate a special case that the number already covers.
 *
 * Strictly `=== 0` (AC-3): the balance is a server-side decimal, so a settled project is exactly
 * zero. `null` / still-loading keeps showing `—`, and an over-invoiced project is negative — a
 * distinct state with its own credit-note prompt, not this one.
 */
export function hasNothingLeftToInvoice(totalRemaining: number | null | undefined): boolean {
  return totalRemaining === 0
}

/**
 * SOLY-REGIE-002 — ce que le bloc « Reste à facturer » DIT quand le solde est à zéro.
 *
 * Quatre situations arrivent au même nombre, et la QA staging du 20/08 a montré que le défaut le
 * plus cher n'était pas un manque mais une AFFIRMATION FAUSSE : « Rien à facturer » sur un travail
 * qui vient d'être effectué ne se contente pas de ne pas rappeler, il dit l'inverse du vrai et
 * autorise l'oubli.
 *
 * L'ordre des branches est la règle, pas un détail d'écriture :
 *  1. `decided`  — une décision a été prise ; elle prime sur tout constat.
 *  2. `to-price` — un fait daté appelle un geste.
 *  3. `not-priced-yet` / `nothing-to-invoice` — les deux phrases existantes, inchangées.
 *
 * ⚠️ `nothing-to-invoice` reste MOT POUR MOT la phrase de la vente annulée (SOLY-ORDER-002
 * fiche 16) : là, le montant AVAIT été défini, c'est la vente qui a été rappelée. C'est la
 * régression la plus facile à introduire dans ce bloc.
 *
 * Et cette phrase-là n'est PAS gardée ici par un cas particulier : c'est le backend qui décide.
 * `InterventionPricingRule` exclut les dossiers dont une vente a existé — annulée comprise — donc
 * `workCompletedOn` revient nul et la branche `to-price` ne s'ouvre pas. Un `isSaleCancelled`
 * ajouté ici ferait dire à la fiche l'inverse de ce que dit « À facturer » dès que les deux
 * règles divergeraient d'un pouce.
 */
export type InvoicingSummaryState =
  | 'decided'
  | 'to-price'
  | 'not-priced-yet'
  | 'nothing-to-invoice'

export function resolveInvoicingSummaryState(input: {
  /** Nul sur un chantier — c'est ce qui garantit qu'aucun état neuf n'y apparaît. */
  interventionPricing: { workCompletedOn: string | null; notBilledAt: string | null } | null
  /** Le total de référence n'a jamais été fixé. */
  nothingPricedYet: boolean
}): InvoicingSummaryState {
  const pricing = input.interventionPricing

  if (pricing?.notBilledAt) return 'decided'
  if (pricing?.workCompletedOn) return 'to-price'
  return input.nothingPricedYet ? 'not-priced-yet' : 'nothing-to-invoice'
}
